import { number, string, z, literal } from 'zod'
import { enqueueSnackbar, SnackbarProvider } from 'notistack'
import { useEffect, useState } from 'react'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import DashboardLayout from '../components/DashboardLayout'
import Paper from '@mui/material/Paper/Paper'
import Stack from '@mui/material/Stack/Stack'
import TextField from '@mui/material/TextField/TextField'
import Checkbox from '@mui/material/Checkbox/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel'
import timezones from 'tzdata'
import { Autocomplete, Typography } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import DomainAdd from '@mui/icons-material/DomainAdd'
import useForm from '@hooks/useForm'
import API from '@enums/api'
import { describeError } from '@utils/errors'

const tzArr = Object.keys(timezones.zones)
const CountryCodes = ['USA', 'CAN', 'UK']

export type NewOrg = {
    name: string
    active: 0 | 1
    limit_users: number
    address?: string
    state?: string
    city?: string
    postcode?: string
    country?: string
    department?: string
    team_id?: string
    timezone?: string
}

const defaultOrg: NewOrg = {
    name: '',
    active: 1,
    limit_users: -1,
}

const orgSchema = z.object({
    name: string()
        .min(3, 'Organization name must be at least 3 characters.')
        .regex(
            /^[a-zA-Z\s\-\d']+$/,
            'Organization name should only contain numbers, letters, spaces, apostrophes, and hyphens.'
        ),
    active: literal(0).or(literal(1)),
    limit_users: number().finite().int().gte(-1),
    address: string()
        .min(3, 'Address should be at least 3 characters.')
        .optional(),
    state: string().min(2, 'State should be at least 2 characters.').optional(),
    city: string().min(3, 'City should be at least 3 characters.').optional(),
    postcode: string()
        .min(3, 'Postcode should be at least 3 characters.')
        .regex(
            /(\b(?:[A-Z]{1,2}[0-9][A-Z0-9]?[ ]?[0-9][A-Z]{2})\b|\b(?:[0-9]{5}(?:-[0-9]{4})?)\b|\b(?:[A-Z][0-9][A-Z] [0-9][A-Z][0-9])\b)/,
            'Postcode does not match the format for USA, UK, or Canadian post - or zip code'
        )
        .optional(),
    country: string()
        .min(2, 'Country should be at least 2 characters.')
        .optional(),
    department: string()
        .min(3, 'Department should be at least 3 characters.')
        .optional(),
    team_id: string().min(3).optional(),
    time_zone: string()
        .min(3, 'Time Zone should be at least 3 characters.')
        .optional(),
})

export default function CreateNewOrgPage() {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)
    const [formValid, setFormValid] = useState(false)
    const [shouldValidate, setShouldValidate] = useState(false)
    const {
        values: validationErrors,
        setValue: setValidationErrors,
        resetForm: resetErrors,
    } = useForm<{ [key in keyof Partial<NewOrg>]: string | undefined }>({})

    const [org, setOrg] = useState(defaultOrg)

    useEffect(() => {
        if (shouldValidate) {
            const debounceTimeout = setTimeout(() => {
                const validatedOrg = validateOrg(org)
                if (validatedOrg === 'error') {
                    setFormValid(false)
                } else {
                    setFormValid(true)
                }
            }, 300) // Debounce duration in milliseconds
            return () => clearTimeout(debounceTimeout)
        }
    }, [org, shouldValidate])

    const validateOrg = (org: NewOrg) => {
        const parsed = orgSchema.safeParse(org)
        resetErrors()
        if (!parsed.error) {
            return 'valid'
        } else {
            // Format error messages as bullet points
            const { fieldErrors } = parsed.error.formErrors
            for (const key of Object.keys(fieldErrors)) {
                setValidationErrors(
                    key as keyof NewOrg,
                    fieldErrors[key as keyof typeof fieldErrors]?.map(
                        (error) => `• ${error}`
                    )[0]
                )
            }

            return 'error'
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        // call the endpoint to add the new admin
        const res = await API.actions.createNewOrg(org)

        if (!res.ok) {
            setError(describeError(res, 'Failed to create the organisation'))
            setLoading(false)
            return
        }

        setSuccess(true)
        resetForm()
    }

    const resetForm = () => {
        // reset the form
        setOrg(defaultOrg)
        setLoading(false)
    }

    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (success) {
        enqueueSnackbar('New admin added successfully', { variant: 'success' })
        setSuccess(false)
    }

    return (
        <DashboardLayout>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: 64,
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        minWidth: '50%',
                        maxWidth: '90%',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body1" sx={{ marginBottom: 8 }}>
                        Use this form to create a new organization in Habitat
                        Admin.
                    </Typography>
                    <form onChange={() => setShouldValidate(true)}>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4 }}
                        >
                            <TextField
                                type="text"
                                label="Organization Name"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        name: e.target.value,
                                    })
                                }
                                value={org.name}
                                fullWidth
                                required
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                            ></TextField>
                        </Stack>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4, height: 56 }}
                        >
                            <FormControlLabel
                                label="Unlimited Users?"
                                required
                                style={{
                                    width: '100%',
                                }}
                                control={
                                    <Checkbox
                                        value={org.limit_users === -1}
                                        defaultChecked
                                        onChange={(e) => {
                                            setOrg({
                                                ...org,
                                                limit_users: e.target.checked
                                                    ? -1
                                                    : 1,
                                            })
                                        }}
                                    />
                                }
                            ></FormControlLabel>
                            {org.limit_users !== -1 ? (
                                <TextField
                                    type="number"
                                    label="User Limit"
                                    onChange={(e) =>
                                        setOrg({
                                            ...org,
                                            limit_users: Number(e.target.value),
                                        })
                                    }
                                    InputProps={{ inputProps: { min: 1 } }}
                                    value={org.limit_users}
                                    fullWidth
                                    error={!!validationErrors.limit_users}
                                    helperText={validationErrors.limit_users}
                                    required
                                ></TextField>
                            ) : (
                                <></>
                            )}
                        </Stack>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4 }}
                        >
                            <TextField
                                type="text"
                                label="Street Address"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        address: e.target.value,
                                    })
                                }
                                value={org.address}
                                error={!!validationErrors.address}
                                helperText={validationErrors.address}
                                fullWidth
                            ></TextField>
                        </Stack>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4 }}
                        >
                            <TextField
                                type="text"
                                label="City"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        city: e.target.value,
                                    })
                                }
                                value={org.city}
                                error={!!validationErrors.city}
                                helperText={validationErrors.city}
                                fullWidth
                            ></TextField>
                            <TextField
                                type="text"
                                label="State"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        state: e.target.value,
                                    })
                                }
                                value={org.state}
                                fullWidth
                            ></TextField>
                        </Stack>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4 }}
                        >
                            <Autocomplete
                                disablePortal
                                options={CountryCodes}
                                renderInput={(params) => (
                                    <TextField {...params} label="Country" />
                                )}
                                onChange={(_e, value) =>
                                    setOrg({
                                        ...org,
                                        country: value ?? 'USA',
                                    })
                                }
                                fullWidth
                            />

                            {/* <TextField
                                type="text"
                                label="Country"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        country: e.target.value,
                                    })
                                }
                                value={org.country}
                                fullWidth
                            ></TextField> */}
                            <TextField
                                type="text"
                                label="Postcode"
                                onChange={(e) =>
                                    setOrg({
                                        ...org,
                                        postcode: e.target.value,
                                    })
                                }
                                value={org.postcode}
                                error={!!validationErrors.postcode}
                                helperText={validationErrors.postcode}
                                fullWidth
                            ></TextField>
                        </Stack>
                        <Stack
                            spacing={2}
                            direction="row"
                            sx={{ marginBottom: 4 }}
                        >
                            <Autocomplete
                                disablePortal
                                options={tzArr}
                                renderInput={(params) => (
                                    <TextField {...params} label="Time Zone" />
                                )}
                                onChange={(_e, value) =>
                                    setOrg({
                                        ...org,
                                        timezone: value ?? undefined,
                                    })
                                }
                                fullWidth
                            />
                        </Stack>
                        <LoadingButton
                            disabled={!formValid}
                            startIcon={<DomainAdd />}
                            size="small"
                            variant="contained"
                            onClick={handleSubmit}
                        >
                            Create New Organization
                        </LoadingButton>
                    </form>
                </Paper>
            </div>
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}
